import React from "react";
import { formatDate, formatMoney, formatNumber } from "@/lib/utils";
import { numberToVietnameseWords } from "@/lib/numberToWords";
import { getCompanyConfig } from "@/config/company";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface WarehouseDocumentPrintTemplateProps {
  document: any;
  config?: any;
  typeLabel?: string;
  hidePrices?: boolean;
}

export default function WarehouseDocumentPrintTemplate({
  document,
  config,
  typeLabel,
  hidePrices = false,
}: WarehouseDocumentPrintTemplateProps) {
  if (!document) return null;

  const c = getCompanyConfig(config);
  const isReceipt = document.type === "RECEIPT";
  const isTransfer = document.type === "TRANSFER";
  const isStocktake =
    typeLabel === "PHIẾU KIỂM KÊ" ||
    typeLabel === "PHIẾU KIỂM KÊ / ĐIỀU CHỈNH" ||
    document.type === "STOCKTAKE";

  const title =
    typeLabel ||
    (isReceipt
      ? "PHIẾU NHẬP KHO"
      : isTransfer
      ? "PHIẾU CHUYỂN KHO"
      : isStocktake
      ? "BIÊN BẢN KIỂM KÊ KHO"
      : "PHIẾU XUẤT KHO");

  const docNo =
    document.documentNo ||
    document.docNo ||
    document.code ||
    (document.id?.length > 20 ? document.id.slice(0, 8).toUpperCase() : document.id) ||
    "KHO";

  const lines: any[] = document.lines || document.items || [];
  const showPriceCols = !isStocktake && !hidePrices;

  const totalQty = lines.reduce(
    (sum, l) => sum + (Number(l.quantity) || Number(l.systemQty) || 0),
    0
  );
  const totalAmount = lines.reduce(
    (sum, l) =>
      sum +
      (Number(l.amount) ||
        (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0)),
    0
  );

  const creatorName =
    document.creator?.fullName ||
    document.user?.fullName ||
    (document.createdBy && !document.createdBy.startsWith("cm")
      ? document.createdBy
      : "Thủ kho APV");

  return (
    <div
      className="p-[10mm] text-black leading-snug text-[12px] bg-white w-full mx-auto min-h-[297mm]"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      {/* ========== COMPANY HEADER ========== */}
      <ISOPrintHeader
        config={config}
      />

      {/* ========== TITLE (CENTERED, NO QR) ========== */}
      <PrintDocumentTitle
        title={title}
        subtitle={
          isReceipt
            ? "GOODS RECEIPT NOTE"
            : isTransfer
            ? "WAREHOUSE TRANSFER NOTE"
            : isStocktake
            ? "STOCKTAKE AUDIT REPORT"
            : "GOODS ISSUE NOTE"
        }
        docNo={docNo}
        date={
          document.date ||
          document.actualDate ||
          document.checkDate ||
          document.createdAt ||
          new Date()
        }
        dateLabel="Ngày lập"
      />

      {/* ========== DOCUMENT METADATA (BORDERLESS 2-COL TABS) ========== */}
      <div className="mb-4">
        <PrintInfoGrid
          labelWidth="w-[120px]"
          leftColumn={[
            {
              label: "Kho hàng",
              value: document.warehouse?.name ||
                document.sourceWarehouse?.name ||
                "Kho Chính APV",
              isBold: true,
            },
            {
              label: isReceipt
                ? "Nhà cung cấp"
                : isTransfer
                ? "Kho đích nhận"
                : "Nơi nhận / Đơn vị",
              value: isReceipt
                ? document.vendor?.name || document.vendorName || "Nhập từ đối tác"
                : isTransfer
                ? document.targetWarehouse?.name || "Kho nhận"
                : document.customer?.name ||
                  document.receiver ||
                  document.referenceNo ||
                  "Xưởng Sản Xuất APV",
              isBold: true,
            },
            {
              label: "Ghi chú",
              value: document.notes || "—",
              colSpan: 2,
            },
          ]}
          rightColumn={[
            {
              label: "Số chứng từ",
              value: docNo,
              isBold: true,
              highlight: true,
            },
            {
              label: "Người lập phiếu",
              value: creatorName,
            },
            {
              label: "Tham chiếu",
              value: document.referenceNo || document.referenceDoc || document.poId || "—",
            },
          ]}
        />
      </div>

      {/* ========== ITEMS TABLE ========== */}
      <table className="w-full border-collapse border border-black mb-2 text-[11px] table-fixed">
        <thead>
          <tr className="bg-slate-100 font-bold text-center">
            <th className="border border-black px-1 py-1.5" style={{ width: "5%" }}>
              STT<br /><i className="font-normal">No.</i>
            </th>
            <th
              className="border border-black px-1.5 py-1.5 text-left"
              style={{ width: "16%" }}
            >
              Mã hàng<br /><i className="font-normal">Item Code</i>
            </th>
            <th
              className="border border-black px-1.5 py-1.5 text-left"
              style={{ width: showPriceCols ? "35%" : "55%" }}
            >
              Tên hàng hóa / Quy cách<br /><i className="font-normal">Description & Specs</i>
            </th>
            <th className="border border-black px-1 py-1.5" style={{ width: "8%" }}>
              ĐVT<br /><i className="font-normal">Unit</i>
            </th>
            <th
              className="border border-black px-1.5 py-1.5 text-center"
              style={{ width: showPriceCols ? "12%" : "16%" }}
            >
              {isStocktake ? "Tồn hệ thống" : "Số lượng"}
              <br /><i className="font-normal">Q'ty</i>
            </th>
            {isStocktake && (
              <>
                <th
                  className="border border-black px-1.5 py-1.5 text-center"
                  style={{ width: "12%" }}
                >
                  Thực tế<br /><i className="font-normal">Actual</i>
                </th>
                <th
                  className="border border-black px-1.5 py-1.5 text-center"
                  style={{ width: "12%" }}
                >
                  Lệch<br /><i className="font-normal">Diff</i>
                </th>
              </>
            )}
            {showPriceCols && (
              <>
                <th
                  className="border border-black px-1.5 py-1.5 text-right"
                  style={{ width: "11%" }}
                >
                  Đơn giá<br /><i className="font-normal">Price</i>
                </th>
                <th
                  className="border border-black px-1.5 py-1.5 text-right"
                  style={{ width: "13%" }}
                >
                  Thành tiền<br /><i className="font-normal">Amount</i>
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {lines.length > 0 ? (
            lines.map((line: any, idx: number) => {
              const itemCode =
                line.material?.code ||
                line.materialItem?.code ||
                line.fg?.code ||
                line.product?.code ||
                line.tooling?.code ||
                line.itemCode ||
                line.item?.code ||
                "—";
              const itemName =
                line.material?.name ||
                line.materialItem?.name ||
                line.fg?.name ||
                line.product?.name ||
                line.tooling?.name ||
                line.itemName ||
                line.item?.name ||
                "Vật tư / Thành phẩm";
              const itemUnit =
                line.material?.unit ||
                line.fg?.unit ||
                line.product?.unit ||
                line.tooling?.unit ||
                line.unit ||
                line.item?.unit ||
                "Cái";
              const qty = Number(line.quantity) || Number(line.systemQty) || 0;
              const unitPrice = Number(line.unitPrice) || 0;
              const amount = Number(line.amount) || qty * unitPrice;

              return (
                <tr key={idx} className="break-inside-avoid">
                  <td className="border border-black px-1 py-1.5 text-center">
                    {idx + 1}
                  </td>
                  <td className="border border-black px-1.5 py-1.5 font-mono text-slate-800">
                    {itemCode}
                  </td>
                  <td className="border border-black px-1.5 py-1.5 text-left">
                    <div className="font-bold text-slate-900">{itemName}</div>
                    {line.notes && (
                      <div className="text-[10px] text-slate-500 italic">
                        {line.notes}
                      </div>
                    )}
                  </td>
                  <td className="border border-black px-1 py-1.5 text-center whitespace-nowrap">
                    {itemUnit}
                  </td>
                  <td className="border border-black px-1.5 py-1.5 text-center font-bold font-mono whitespace-nowrap">
                    {formatNumber(qty)}
                  </td>
                  {isStocktake && (
                    <>
                      <td className="border border-black px-1.5 py-1.5 text-center font-bold font-mono whitespace-nowrap">
                        {formatNumber(line.actualQty ?? 0)}
                      </td>
                      <td
                        className={`border border-black px-1.5 py-1.5 text-center font-bold font-mono whitespace-nowrap ${
                          (line.diffQty || 0) < 0
                            ? "text-red-600"
                            : (line.diffQty || 0) > 0
                            ? "text-emerald-600"
                            : ""
                        }`}
                      >
                        {(line.diffQty || 0) > 0
                          ? `+${formatNumber(line.diffQty)}`
                          : formatNumber(line.diffQty || 0)}
                      </td>
                    </>
                  )}
                  {showPriceCols && (
                    <>
                      <td className="border border-black px-1.5 py-1.5 text-right tabular-nums whitespace-nowrap">
                        {unitPrice > 0 ? formatMoney(unitPrice) : "—"}
                      </td>
                      <td className="border border-black px-1.5 py-1.5 text-right font-bold tabular-nums whitespace-nowrap">
                        {amount > 0 ? formatMoney(amount) : "—"}
                      </td>
                    </>
                  )}
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={showPriceCols ? 7 : isStocktake ? 7 : 5}
                className="border border-black px-2 py-4 text-center text-slate-500 italic"
              >
                Không có dữ liệu mặt hàng
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr className="bg-yellow-50 font-bold">
            <td
              colSpan={4}
              className="border border-black px-2 py-1.5 text-left text-[12px]"
            >
              TỔNG CỘNG:
            </td>
            <td className="border border-black px-1.5 py-1.5 text-center font-mono text-[12px]">
              {formatNumber(totalQty)}
            </td>
            {isStocktake && (
              <td
                colSpan={2}
                className="border border-black px-2 py-1.5 text-center text-[10px] text-slate-500"
              >
                —
              </td>
            )}
            {showPriceCols && (
              <td
                colSpan={2}
                className="border border-black px-2 py-1.5 text-right text-[12px] text-red-700 tabular-nums"
              >
                {formatMoney(totalAmount)}
              </td>
            )}
          </tr>
          {showPriceCols && totalAmount > 0 && (
            <tr>
              <td
                colSpan={7}
                className="border border-black px-2 py-1.5 text-[11px] italic"
              >
                <b>Bằng chữ/ <i>In words</i>:</b>{" "}
                {numberToVietnameseWords(totalAmount, "đồng")}
              </td>
            </tr>
          )}
        </tfoot>
      </table>

      {/* ========== 4-PARTY SIGNATURES ========== */}
      <div className="grid grid-cols-4 gap-2 mt-8 text-[11px] text-center break-inside-avoid">
        <div>
          <div className="font-bold mb-1">NGƯỜI LẬP PHIẾU</div>
          <div className="text-slate-500 italic text-[10px] mb-14">
            (Ký & ghi rõ họ tên)
          </div>
          <div className="font-medium text-slate-800">{creatorName}</div>
        </div>
        <div>
          <div className="font-bold mb-1">
            {isReceipt ? "NGƯỜI GIAO HÀNG" : "NGƯỜI NHẬN HÀNG"}
          </div>
          <div className="text-slate-500 italic text-[10px] mb-14">
            (Ký & ghi rõ họ tên)
          </div>
          <div className="font-medium text-slate-800">....................</div>
        </div>
        <div>
          <div className="font-bold mb-1">THỦ KHO</div>
          <div className="text-slate-500 italic text-[10px] mb-14">
            (Ký & xác nhận nhập/xuất)
          </div>
          <div className="font-medium text-slate-800">....................</div>
        </div>
        <div>
          <div className="font-bold mb-1">KẾ TOÁN KHO / GIÁM ĐỐC</div>
          <div className="text-slate-500 italic text-[10px] mb-14">
            (Duyệt & lưu chứng từ)
          </div>
          <div className="font-medium text-slate-800">....................</div>
        </div>
      </div>
    </div>
  );
}
